// @ts-check
import React, { useState } from 'react';
import { Page, DocumentPreview, Utils, Icon } from 'ecrion-components';
import DocumentTitle from 'react-document-title';
import Snackbar from '@material-ui/core/Snackbar';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { useUser } from '../../context/UserContext';
import { downloadFile } from '../../utils/contentClient';
import {
  getFileFromPath,
  getFileFromJobId,
  getFileFromCommunicationId,
  getFileFromCommunicationName,
} from '../../utils/portalFilesClient';
import Communication from '../Communication';
import PageNotFound from '../PageNotFound';
import Alert from '../Alert';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

const unsupportedFiles = ['.wk4', '.ehtml', '.rules'];

const useStyles = makeStyles((theme) => {
  return {
    text: {
      margin: '16px',
    },
  };
});

const File = ({ match, history, location }) => {
  const [accessResponse, setAccessResponse] = useState({
    hasAccess: false,
    message: '',
  });
  const [file, setFile] = useState(null);
  const [isDownloadable, setIsDownloadable] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const url = Utils.url.parseUrl(location.search);
  const hashParts = location.hash.split('/');

  const classes = useStyles();
  const user = useUser();
  const userEnvironment = user.Environment;
  const receivedEnvironment = decodeURIComponent(match.params.environment);
  const encodedUserEnvironment = encodeURIComponent(userEnvironment);

  const getExtension = (fileName) => {
    const extensionIndex = fileName.lastIndexOf('.');
    if (extensionIndex === -1) return 'unknown';

    return fileName.substring(extensionIndex);
  };

  const isPreviewable = (fileName) => {
    const extension = getExtension(fileName);
    if (extension === 'unknown') return false;
    return !unsupportedFiles.includes(extension);
  };

  const download = () => {
    downloadFile(hashParts[1], hashParts[2]).catch((err) => {
      setSnackbarMessage(
        'Could not download file: ' + Utils.getPrettyError(err)
      );
      setOpenSnackbar(true);
    });
  };

  if (receivedEnvironment !== userEnvironment) {
    return <PageNotFound history={history} />;
  }

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleRequestError = (err) => {
    setAccessResponse({ hasAccess: false, message: Utils.getPrettyError(err) });
  };

  const getCategory = (file) => {
    if (file != null) {
      const categoryTag = file.Tags.find(
        (tag) => tag.Name === 'Portal Category'
      );
      if (categoryTag) {
        const category = categoryTag.Value.toLowerCase();
        if (category === 'analytics') return 'dashboards';
        return category;
      }
    }
    return null;
  };

    const getToolbarTitle = () => {
    if (fileTitle) return fileTitle;
    if (accessResponse.message) return 'Unknown Communication';
    return <CircularProgress size={20} />;
  };

  const getFileTitle = (file) => {
    let urlTitle = '';
    if (
      url.query.jobId ||
      url.query.communicationId ||
      url.query.communication
    ) {
      urlTitle = url.query.title;
    } else {
      urlTitle = url.query.title || url.query.file;
    }

    if (urlTitle) {
      const extensionIndex = urlTitle.lastIndexOf('.');
      if (extensionIndex !== -1)
        urlTitle = urlTitle.substring(0, extensionIndex);
      return urlTitle;
    }

    if (file) {
      const titleTag = file.Tags.find((tag) => tag.Name === 'Title');
      if (titleTag) {
        return titleTag.Value;
      }
    }

    return '';
  };
  const fileTitle = getFileTitle(file);

  const handleRequestSuccess = (file) => {
    setFile(file);

    setAccessResponse({ hasAccess: true, message: '' });
    if (url.query.file) {
      setIsDownloadable(
        url.query.file.endsWith('.pdf') === true ||
          url.query.file.endsWith('.docx') === true
      );
    }
  };

  const getPreviousLocation = () => {
    if (location.state && location.state.from) {
      return {
        ...location.state.from,
        state: {},
      };
    }
    return null;
  };

  const closeFile = () => {
    const previousLocation = getPreviousLocation();
    if (previousLocation) {
      history.push(previousLocation);
      return;
    }

    const fileCategory = getCategory(file);
    if (fileCategory) {
      history.push(`/${encodedUserEnvironment}/${fileCategory}`);
      return;
    }

    history.push(`/`);
  };

  React.useEffect(() => {
    if (hashParts[0] === '#workspace') {
      if (hashParts.length !== 3) return;

      const workspace = hashParts[1];
      const path = hashParts[2];

      getFileFromPath(path, workspace)
        .then((file) => handleRequestSuccess(file))
        .catch((err) => handleRequestError(err));

      return;
    }

    const communicationName = url.query.communication;
    if (communicationName) {
      getFileFromCommunicationName(communicationName)
        .then((file) => handleRequestSuccess(file))
        .catch((err) => handleRequestError(err));

      return;
    }

    const jobId = url.query.jobId;
    const communicationId = url.query.communicationId;

    if (jobId && communicationId) {
      getFileFromJobId(jobId, communicationId)
        .then((file) => handleRequestSuccess(file))
        .catch((err) => handleRequestError(err));

      return;
    }

    if (communicationId) {
      getFileFromCommunicationId(communicationId)
        .then((file) => handleRequestSuccess(file))
        .catch((err) => handleRequestError(err));
      return;
    }

    setAccessResponse({
      hasAccess: false,
      message: 'Could not access the file.',
    });
  }, []);

  if (
    !url.query.jobId &&
    !url.query.communicationId &&
    !url.query.communication
  )
    return (
      <DocumentTitle title={`${fileTitle || 'File'} - Customer Portal`}>
        <div className="default-file">
          <Page.ToolBar
            color="navbar"
            icon={{
              type: 'close',
              onClick: closeFile,
            }}
            leftChildren={fileTitle}
            centerChildren={
              isDownloadable && (
                <Button
                  className="file-download-button"
                  color="inherit"
                  startIcon={<Icon type="download" />}
                  onClick={() => download()}
                >
                  Download
                </Button>
              )
            }
          />
          {accessResponse.hasAccess ? (
            <Page.Common margin="none">
              {
                <>
                  {hashParts.length !== 3 ? (
                    <Alert>Could not preview the file.</Alert>
                  ) : isPreviewable(url.query.file) ? (
                    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}>
                    <DocumentPreview
                      className="file-preview"
                      workspace={hashParts[1]}
                      path={hashParts[2]}
                      fileName={url.query.file}
                    />
                    </div>
                  ) : (
                    <Typography
                      align="left"
                      variant="body2"
                      className={`${classes.text} file-message`}
                    >
                      {`Preview is not available for files of this format: "${getExtension(
                        url.query.file
                      )}".`}
                    </Typography>
                  )}
                </>
              }
            </Page.Common>
          ) : (
            accessResponse.message && <Alert>{accessResponse.message}</Alert>
          )}
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            message={snackbarMessage}
            action={<Icon type="close" onClick={handleSnackbarClose} />}
          />
        </div>
      </DocumentTitle>
    );

  return (
    <div className="file-communication-wrapper">
      <DocumentTitle title={`${fileTitle || 'File'} - Customer Portal`} />
      {accessResponse.hasAccess ? (
        <Communication.Private
          title={fileTitle}
          communicationName={url.query.communication}
          communicationId={url.query.communicationId}
          jobId={url.query.jobId}
          user={user}
          onClose={closeFile}
        />
      ) : (
        <>
          <Page.ToolBar
            color="navbar"
            icon={{
              type: 'close',
              onClick: closeFile,
            }}
            leftChildren={getToolbarTitle()}
          />
          {accessResponse.message && <Alert>{accessResponse.message}</Alert>}
        </>
      )}
    </div>
  );
};

export default File;
